import React, { useEffect, useState } from "react";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CAlert,
  CSpinner,
  CCardHeader,
  CRow,
  CCol,
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormCheck,
  CBadge,
  CFormSelect,
  CInputGroup,
  CInputGroupText,
  CFormInput,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import {
  cilCalendar,
  cilDollar,
  cilStorage,
  cilCheckCircle,
  cilWarning,
  cilLayers,
} from "@coreui/icons";
import quickbooksService from "../services/quickbooksService";
import { useNavigate } from "react-router-dom";

const QuickBooksRenewalModal = ({
  show,
  onClose,
  order,
  products = [],
  reseller = null,
  onSecurePayment,
  isSuperAdmin = false,
}) => {
  const navigate = useNavigate();
  const [selectedStorage, setSelectedStorage] = useState("");
  const [includeCartridge, setIncludeCartridge] = useState(false);
  const [selectedCartridgeProductId, setSelectedCartridgeProductId] =
    useState("");
  const [loading, setLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [error, setError] = useState("");
  const [isPaymentVerified, setIsPaymentVerified] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);

  const [paymentTerms, setPaymentTerms] = useState("immediate");
  const [isTaxExempt, setIsTaxExempt] = useState(false);
  const [taxExemptReason, setTaxExemptReason] = useState("");
  const [customAmount, setCustomAmount] = useState(null);
  const [useCustomAmount, setUseCustomAmount] = useState(false);

  // Reset state when modal opens/closes or order changes
  useEffect(() => {
    if (show && order) {
      if (!invoiceData) {
        setSelectedStorage("");
        setIncludeCartridge(false);
        setSelectedCartridgeProductId("");
        setError("");
        setLoading(false);
        setIsPaymentVerified(false);
        setCustomAmount(null);
        setUseCustomAmount(false);

        // ✅ AUTOMATICALLY SET PAYMENT TERMS BASED ON RESELLER'S PAYMENT_MODE
        if (reseller && reseller.payment_mode) {
          const paymentMode = reseller.payment_mode;
          let terms = "immediate";

          switch (paymentMode) {
            case "Credit Card":
            case "ACH":
              terms = "immediate";
              break;
            case "Net 30":
              terms = "net_30";
              break;
            case "Net 45":
              terms = "net_45";
              break;
            case "Net 60":
              terms = "net_60";
              break;
            case "Net 65":
              terms = "net_60";
              break;
            default:
              terms = "immediate";
          }

          setPaymentTerms(terms);
          console.log(
            `✅ Auto-set renewal payment terms to: ${terms} based on reseller payment mode: ${paymentMode}`,
          );
        }

        setIsTaxExempt(false);
        setTaxExemptReason("");
      }

      // Clear any existing polling
      if (pollingInterval) {
        clearInterval(pollingInterval);
        setPollingInterval(null);
      }
    }
  }, [show, order, reseller]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // Calculate partner discount amounts
  const calculatePartnerAmount = (customerAmount) => {
    if (!reseller || !reseller.margin)
      return customerAmount * (order?.quantity || 1);

    const margin = reseller.margin;
    const discountRate = margin / 100;
    return customerAmount * (order?.quantity || 1) * (1 - discountRate);
  };

  const calculateDiscountAmount = (customerAmount) => {
    if (!reseller || !reseller.margin) return 0;

    const margin = reseller.margin;
    const discountRate = margin / 100;
    return customerAmount * (order?.quantity || 1) * discountRate;
  };

  // Detect product market
  const detectProductMarket = () => {
    if (!order || !order.subscription_plan) return "commercial";

    const subscriptionPlan = order.subscription_plan.toLowerCase();
    const productName = order.product_name?.toLowerCase() || "";

    if (subscriptionPlan.includes("gsa") || productName.includes("gsa")) {
      return "gsa";
    }

    return "commercial";
  };

  // Check if product can have cartridges
  const canHaveCartridges = () => {
    if (!order || !order.subscription_plan) return false;

    const subscriptionPlan = order.subscription_plan.toLowerCase();

    if (subscriptionPlan.includes("unlimited")) {
      return false;
    }

    if (
      subscriptionPlan.includes("gb") ||
      subscriptionPlan.includes("tb") ||
      subscriptionPlan.includes("mb")
    ) {
      return true;
    }

    return false;
  };

  // Get available cartridge products
  const getAvailableCartridgeProducts = () => {
    if (!products.length) return [];

    const productMarket = detectProductMarket();

    const cartridgeProducts = products.filter(
      (p) =>
        p.type === "Cartridge" &&
        p.market &&
        p.market.toLowerCase() === productMarket.toLowerCase(),
    );

    return cartridgeProducts;
  };

  // Payment status checking
  const checkPaymentStatus = async (invoiceId) => {
    try {
      const response = await quickbooksService.checkPaymentStatus(invoiceId);

      if (response.success) {
        if (response.payment_status === "paid") {
          navigate("/ThankYouPage", {
            state: {
              invoiceId: invoiceId,
              invoiceNumber: response.invoice_number,
              customerName: response.customer_name,
              amounts: response.amounts,
              isRenewal: true,
              isTaxExempt: isTaxExempt,
              taxExemptReason: taxExemptReason,
            },
          });

          if (pollingInterval) {
            clearInterval(pollingInterval);
            setPollingInterval(null);
          }

          handleCloseModal();

          return "paid";
        }
        return response.payment_status;
      }
      return "unknown";
    } catch (error) {
      console.error("Error checking renewal payment status:", error);
      return "error";
    }
  };

  // Start payment polling
  const startPaymentPolling = (invoiceId) => {
    const interval = setInterval(() => {
      checkPaymentStatus(invoiceId).then((status) => {
        if (status === "paid") {
        }
      });
    }, 5000);

    setPollingInterval(interval);

    setTimeout(
      () => {
        clearInterval(interval);
        setPollingInterval(null);
      },
      10 * 60 * 1000,
    );
  };

  // Handle modal close
  const handleCloseModal = () => {
    setSelectedStorage("");
    setIncludeCartridge(false);
    setSelectedCartridgeProductId("");
    setInvoiceData(null);
    setError("");
    setLoading(false);
    setIsPaymentVerified(false);
    setPaymentTerms("immediate");
    setIsTaxExempt(false);
    setTaxExemptReason("");
    setCustomAmount(null);
    setUseCustomAmount(false);

    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }

    onClose();
  };

  const availableCartridgeProducts = getAvailableCartridgeProducts();
  const showCartridgeSection =
    canHaveCartridges() && availableCartridgeProducts.length > 0;
  const selectedCartridgeProduct = availableCartridgeProducts.find(
    (p) => p.softwareid === selectedCartridgeProductId,
  );
  const productMarket = detectProductMarket();

  // Format price function
  const formatPrice = (price) => {
    if (!price && price !== 0) return "$0.00";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(price);
  };

  // Handle custom amount toggle
  const handleCustomAmountToggle = (checked) => {
    setUseCustomAmount(checked);
    if (!checked) {
      setCustomAmount(null);
    } else if (!customAmount) {
      const pricing = calculatePricing();
      setCustomAmount(pricing.totalCustomerAmount);
    }
  };

  // Handle custom amount change
  const handleCustomAmountChange = (amount) => {
    setCustomAmount(amount);
  };

  // Calculate pricing breakdown
  const calculatePricing = () => {
    if (!order) return null;

    const quantity = order.quantity || 1;

    const baseCustomerAmount = order.renewal_amount_customer || 0;
    const customerAmount =
      useCustomAmount && customAmount !== null
        ? customAmount
        : baseCustomerAmount * quantity;

    const partnerAmount = calculatePartnerAmount(
      useCustomAmount ? customAmount / quantity : baseCustomerAmount,
    );
    const discountAmount = calculateDiscountAmount(
      useCustomAmount ? customAmount / quantity : baseCustomerAmount,
    );

    const cartridgeCustomerPrice =
      includeCartridge && selectedCartridgeProduct
        ? selectedCartridgeProduct.price * quantity
        : 0;
    const cartridgePartnerPrice = calculatePartnerAmount(
      includeCartridge && selectedCartridgeProduct
        ? selectedCartridgeProduct.price
        : 0,
    );
    const cartridgeDiscountAmount = calculateDiscountAmount(
      includeCartridge && selectedCartridgeProduct
        ? selectedCartridgeProduct.price
        : 0,
    );

    const totalCustomerAmount = customerAmount + cartridgeCustomerPrice;
    const totalPartnerAmount = partnerAmount + cartridgePartnerPrice;
    const totalDiscountAmount = discountAmount + cartridgeDiscountAmount;

    return {
      customerAmount,
      partnerAmount,
      discountAmount,
      cartridgeCustomerPrice,
      cartridgePartnerPrice,
      cartridgeDiscountAmount,
      totalCustomerAmount,
      totalPartnerAmount,
      totalDiscountAmount,
      discountPercentage: reseller?.margin || 0,
      useCustomAmount,
      baseAmount: baseCustomerAmount,
      quantity: quantity,
    };
  };

  const handleCartridgeToggle = (checked) => {
    setIncludeCartridge(checked);
    if (!checked) {
      setSelectedCartridgeProductId("");
    } else if (
      availableCartridgeProducts.length > 0 &&
      !selectedCartridgeProductId
    ) {
      setSelectedCartridgeProductId(availableCartridgeProducts[0].softwareid);
    }
  };

  // Tax exemption handler
  const handleTaxExemptToggle = (checked) => {
    setIsTaxExempt(checked);
    if (!checked) {
      setTaxExemptReason("");
    }
  };

  // Success View Component
  const SuccessView = ({
    invoiceData,
    paymentTerms,
    onDirectPayment,
    onClose,
  }) => {
    const pricing = calculatePricing();

    return (
      <CRow>
        <CCol>
          {/* DIFFERENT MESSAGE FOR NET TERMS VS ACH AND CREDIT CARD */}
          {paymentTerms !== "immediate" ? (
            <CAlert color="success" className="mb-4">
              <h6>✅ Subscription Renewed Successfully!</h6>
              <p className="mb-1">
                <strong>Payment Terms:</strong> {reseller?.payment_mode}
              </p>
              <p className="mb-1">
                <strong>Customer Email:</strong> {invoiceData.customer_email}
              </p>
              <p className="mb-1">
                <strong>Partner Email:</strong> {invoiceData.partner_email}
              </p>
              <p className="mb-1">
                <strong>Subscription Status:</strong>{" "}
                <CBadge color="success">Renewed & Active</CBadge>
              </p>
              <p className="mb-1">
                <strong>New End Date:</strong>{" "}
                {new Date(
                  Date.now() + 365 * 24 * 60 * 60 * 1000,
                ).toLocaleDateString()}
              </p>
              <p className="mb-1">
                <strong>Amount Due:</strong>{" "}
                {formatPrice(
                  invoiceData.initial_amount_partner ||
                    pricing?.totalPartnerAmount,
                )}
              </p>
              <p className="mb-2">
                <strong>Due Date:</strong>{" "}
                {invoiceData.payment_due_date
                  ? new Date(invoiceData.payment_due_date).toLocaleDateString()
                  : new Date(
                      Date.now() +
                        parseInt(paymentTerms.replace("net_", "")) *
                          24 *
                          60 *
                          60 *
                          1000,
                    ).toLocaleDateString()}
              </p>
              <p className="mb-0 text-info">
                <strong>
                  ✅ Your subscription has been renewed!. Payment due in{" "}
                  {paymentTerms.replace("net_", "")} days.
                </strong>
              </p>
            </CAlert>
          ) : (
            <CAlert color="success" className="mb-4">
              <h6>✅ Renewal Payment Ready!</h6>
              <p className="mb-1">
                <strong>Payment Terms:</strong> {reseller?.payment_mode}
              </p>
              <p className="mb-1">
                <strong>Invoice Number:</strong>{" "}
                {invoiceData.invoice_number || invoiceData.invoice_id}
              </p>
              <p className="mb-1">
                <strong>Customer Email:</strong> {invoiceData.customer_email}
              </p>
              <p className="mb-1">
                <strong>Partner Email:</strong> {invoiceData.partner_email}
              </p>
              <p className="mb-1">
                <strong>Billing Email:</strong> {invoiceData.billing_email}
              </p>
              <p className="mb-1">
                <strong>Subscription:</strong> {order.subscription_number}
              </p>
              <p className="mb-1">
                <strong>Original Price:</strong>{" "}
                {formatPrice(
                  invoiceData.amounts?.customer_amount ||
                    pricing?.totalCustomerAmount,
                )}
              </p>
              <p className="mb-1">
                <strong>Partner Discount:</strong> -
                {formatPrice(
                  invoiceData.amounts?.discount_amount ||
                    pricing?.totalDiscountAmount,
                )}{" "}
                ({reseller?.margin}%)
              </p>
              <p className="mb-1">
                <strong>Subtotal:</strong>{" "}
                {formatPrice(
                  invoiceData.amounts?.partner_amount ||
                    pricing?.totalPartnerAmount,
                )}
              </p>
              <p className="mb-1">
                <strong>Sales Tax:</strong>{" "}
                {invoiceData.is_tax_exempt ? (
                  <span className="text-success">$0.00 (Tax Exempt)</span>
                ) : (
                  formatPrice(invoiceData.amounts?.tax_amount)
                )}
              </p>
              {invoiceData.is_tax_exempt && (
                <p className="mb-1">
                  <strong>Tax Exemption:</strong>{" "}
                  {invoiceData.tax_exempt_reason}
                </p>
              )}
              <p className="mb-2 fs-6">
                <strong>Total Due:</strong>{" "}
                {formatPrice(
                  invoiceData.amounts?.total_due || pricing?.totalPartnerAmount,
                )}
              </p>
              <p className="mb-0 text-info">
                <strong>
                  Secure ACH and Credit Card payment processing via QuickBooks
                </strong>
              </p>
            </CAlert>
          )}

          {/* DIFFERENT BUTTONS FOR NET TERMS VS ACH AND CREDIT CARD */}
          <CRow className="text-center">
            <CCol className="mb-3">
              {paymentTerms !== "immediate" ? (
                <div>
                  <CButton
                    color="success"
                    className="w-100 mb-2"
                    onClick={onClose}
                    size="lg"
                  >
                    <CIcon icon={cilCheckCircle} className="me-2" />
                    Done - Subscription Renewed
                  </CButton>
                  <small className="text-muted d-block">
                    Your subscription has been renewed. End date extended by 365
                    days. Payment due in {paymentTerms.replace("net_", "")}{" "}
                    days.
                  </small>
                </div>
              ) : (
                <div>
                  <CButton
                    color="primary"
                    className="w-100"
                    onClick={onDirectPayment}
                    size="lg"
                    disabled={!invoiceData.payment_link}
                  >
                    <CIcon icon={cilDollar} className="me-2" />
                    {invoiceData.payment_link
                      ? "Pay with ACH or Credit Card"
                      : "Processing Payment Link..."}
                  </CButton>
                  <small className="text-muted d-block mt-2">
                    Secure payment via ACH bank transfer or credit card
                  </small>
                </div>
              )}
            </CCol>
          </CRow>
        </CCol>
      </CRow>
    );
  };

  // Product Selection View Component
  const ProductSelectionView = () => {
    const pricing = calculatePricing();
    const quantity = order?.quantity || 1;

    return (
      <>
        {/* Automatic Payment Terms Information */}
        <CRow className="mb-4">
          <CCol>
            <CCard className="bg-info text-white">
              <CCardBody>
                <p className="mb-2">
                  <strong>Your Account Payment Mode:</strong>{" "}
                  {reseller?.payment_mode}
                </p>
                {isSuperAdmin && (
                  <p className="mb-0">
                    <strong>Super Admin Mode:</strong> Renewing on behalf of{" "}
                    {reseller?.name}
                  </p>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* Order Information */}
        <CRow className="mb-4">
          <CCol>
            <CCard className="bg-light">
              <CCardBody>
                <h6>Order Details</h6>
                <CRow>
                  <CCol md={6}>
                    <p>
                      <strong>Account:</strong> {order.account_name}
                    </p>
                    <p>
                      <strong>Product:</strong> {order.product_name}
                    </p>
                    <p>
                      <strong>Plan:</strong> {order.subscription_plan}
                    </p>
                    <p>
                      <strong>Market:</strong>
                      <span
                        className={`badge bg-${
                          productMarket === "gsa" ? "primary" : "success"
                        } ms-2`}
                      >
                        {productMarket.toUpperCase()}
                      </span>
                    </p>
                    {showCartridgeSection && (
                      <p>
                        <strong>Storage Type:</strong>
                        <span className="badge bg-info ms-2">
                          Limited Storage - Can Add Cartridge
                        </span>
                      </p>
                    )}
                  </CCol>
                  <CCol md={6}>
                    <p>
                      <strong>Subscription #:</strong>{" "}
                      {order.subscription_number}
                    </p>
                    <p>
                      <strong>End Date:</strong>{" "}
                      {new Date(
                        order.end_date?.$date || order.end_date,
                      ).toLocaleDateString()}
                    </p>
                    <p>
                      <strong>Status:</strong>{" "}
                      <span className="text-success">Active</span>
                    </p>
                    <p>
                      <strong>Quantity:</strong>{" "}
                      <span className="badge bg-primary">{quantity}</span>
                    </p>
                    {reseller && (
                      <p>
                        <strong>Partner Discount:</strong>
                        <span className="text-success ms-2">
                          {reseller.margin}%
                        </span>
                      </p>
                    )}
                    <p>
                      <strong>Unit Price (Customer):</strong>{" "}
                      {formatPrice(order.renewal_amount_customer)}
                    </p>
                    <p>
                      <strong>Total Base Amount:</strong>{" "}
                      {formatPrice(
                        (order.renewal_amount_customer || 0) * quantity,
                      )}
                    </p>
                  </CCol>
                </CRow>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* NEW: Editable Amount Section */}
        <CRow className="mb-4">
          <CCol>
            <CCard>
              <CCardHeader className="bg-primary text-white">
                <h6 className="mb-0">
                  <CIcon icon={cilDollar} className="me-2" />
                  Amount Configuration
                </h6>
              </CCardHeader>
              <CCardBody>
                {/* Quantity Display */}
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Quantity:</strong>
                      <br />
                      <small className="text-muted">
                        Number of subscriptions to renew
                      </small>
                    </div>
                    <div className="text-end">
                      <span className="badge bg-primary fs-6 p-2">
                        <CIcon icon={cilLayers} className="me-1" />
                        {quantity}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Custom Amount Toggle */}
                <CFormCheck
                  type="checkbox"
                  id="useCustomAmount"
                  label="Use custom amount (override calculated pricing)"
                  checked={useCustomAmount}
                  onChange={(e) => handleCustomAmountToggle(e.target.checked)}
                  className="mb-3"
                />

                {useCustomAmount && (
                  <div className="mb-3">
                    <label htmlFor="customAmount" className="form-label">
                      <strong>Custom Total Amount (Customer)</strong>
                    </label>
                    <CInputGroup>
                      <CInputGroupText>$</CInputGroupText>
                      <CFormInput
                        id="customAmount"
                        type="number"
                        step="0.01"
                        min="0"
                        value={customAmount || ""}
                        onChange={(e) =>
                          handleCustomAmountChange(
                            parseFloat(e.target.value) || 0,
                          )
                        }
                        placeholder="Enter custom total amount"
                      />
                    </CInputGroup>
                    <small className="text-muted">
                      Enter the total customer amount for {quantity}{" "}
                      subscription(s)
                    </small>
                  </div>
                )}

                {/* Amount Summary */}
                <div className="p-3 bg-light rounded">
                  <strong>Amount Summary:</strong>
                  <div className="mt-2">
                    <div className="d-flex justify-content-between">
                      <span>Unit Price:</span>
                      <span>{formatPrice(order.renewal_amount_customer)}</span>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span>Quantity:</span>
                      <span>{quantity}</span>
                    </div>
                    <div className="d-flex justify-content-between border-top pt-1">
                      <span>Base Total:</span>
                      <span>
                        {formatPrice(
                          (order.renewal_amount_customer || 0) * quantity,
                        )}
                      </span>
                    </div>
                    {useCustomAmount && (
                      <div className="d-flex justify-content-between border-top pt-1">
                        <span>Custom Total:</span>
                        <span className="fw-bold text-primary">
                          {formatPrice(customAmount || 0)}
                        </span>
                      </div>
                    )}
                    <div className="d-flex justify-content-between">
                      <span>Partner Discount:</span>
                      <span className="text-success">
                        {reseller?.margin || 0}%
                      </span>
                    </div>
                  </div>
                </div>
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* Tax Exemption Section */}
        <CRow className="mb-4">
          <CCol>
            <CCard>
              <CCardHeader className="bg-info text-white">
                <h6 className="mb-0">
                  <CIcon icon={cilWarning} className="me-2" />
                  Tax Exemption
                </h6>
              </CCardHeader>
              <CCardBody>
                <CFormCheck
                  type="checkbox"
                  id="isTaxExempt"
                  label="This organization is tax exempt (Government, Non-profit, etc.)"
                  checked={isTaxExempt}
                  onChange={(e) => handleTaxExemptToggle(e.target.checked)}
                  className="mb-3"
                />

                {isTaxExempt && (
                  <>
                    <CFormSelect
                      value={taxExemptReason}
                      onChange={(e) => setTaxExemptReason(e.target.value)}
                      className="mb-2"
                    >
                      <option value="">Select tax exemption reason...</option>
                      <option value="Government Organization">
                        Government Organization
                      </option>
                      <option value="Non-profit Organization">
                        Non-profit Organization
                      </option>
                      <option value="Educational Institution">
                        Educational Institution
                      </option>
                      <option value="Religious Organization">
                        Religious Organization
                      </option>
                      <option value="Other Tax Exempt">
                        Other Tax Exempt Entity
                      </option>
                    </CFormSelect>

                    {taxExemptReason && (
                      <div className="mt-2 p-2 bg-light rounded">
                        <small>
                          <strong>Tax Exempt:</strong> {taxExemptReason}
                        </small>
                        <br />
                        <small className="text-success">
                          ✅ No sales tax will be applied to this invoice
                        </small>
                      </div>
                    )}
                  </>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>

        {/* Cartridge Product Selection */}
        {showCartridgeSection && (
          <CRow className="mb-4">
            <CCol>
              <CCard>
                <CCardHeader className="bg-warning text-white">
                  <h6 className="mb-0">
                    <CIcon icon={cilStorage} className="me-2" />
                    Optional {productMarket.toUpperCase()} Cartridge Products (
                    {availableCartridgeProducts.length} available)
                  </h6>
                </CCardHeader>
                <CCardBody>
                  <CFormCheck
                    type="checkbox"
                    id="includeCartridge"
                    label="Add Additional Cartridge Storage"
                    checked={includeCartridge}
                    onChange={(e) => handleCartridgeToggle(e.target.checked)}
                    className="mb-3"
                  />

                  {includeCartridge && (
                    <>
                      <CFormSelect
                        value={selectedCartridgeProductId}
                        onChange={(e) =>
                          setSelectedCartridgeProductId(e.target.value)
                        }
                        className="mb-2"
                      >
                        <option value="">Select a cartridge product...</option>
                        {availableCartridgeProducts.map((cartridge) => (
                          <option
                            key={cartridge.softwareid}
                            value={cartridge.softwareid}
                          >
                            {cartridge.software_name} - {cartridge.subscription}{" "}
                            - {formatPrice(cartridge.price)} per unit
                            {reseller && (
                              <small className="text-muted">
                                {" "}
                                (Partner:{" "}
                                {formatPrice(
                                  calculatePartnerAmount(cartridge.price) /
                                    (order?.quantity || 1),
                                )}
                                )
                              </small>
                            )}
                          </option>
                        ))}
                      </CFormSelect>

                      {selectedCartridgeProduct && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <small>
                            <strong>Selected Cartridge:</strong>{" "}
                            {selectedCartridgeProduct.subscription}
                            <br />
                            <strong>Unit Price:</strong>{" "}
                            {formatPrice(selectedCartridgeProduct.price)} |
                            <strong className="text-success">
                              {" "}
                              Partner Unit Price:{" "}
                              {formatPrice(
                                calculatePartnerAmount(
                                  selectedCartridgeProduct.price,
                                ) / (order?.quantity || 1),
                              )}
                            </strong>
                            <br />
                            <strong>Total Cartridge Price:</strong>{" "}
                            {formatPrice(
                              selectedCartridgeProduct.price *
                                (order?.quantity || 1),
                            )}
                          </small>
                        </div>
                      )}
                    </>
                  )}

                  <small className="text-muted">
                    Cartridge products provide additional storage capacity for
                    limited storage licenses. Only {productMarket.toUpperCase()}{" "}
                    market cartridges are shown.
                    {reseller &&
                      ` ${reseller.margin}% partner discount applied to all cartridge prices.`}
                  </small>
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        )}

        {/* Pricing Breakdown */}
        {pricing && (
          <CRow className="mb-4">
            <CCol>
              <h6>Pricing Breakdown ({quantity} subscription(s))</h6>
              {reseller ? (
                <CAlert color="info" className="mb-3">
                  <small>
                    Partner Discount: <strong>{reseller.margin}%</strong>{" "}
                    applied to all amounts
                  </small>
                </CAlert>
              ) : (
                <CAlert color="warning" className="mb-3">
                  <small>Loading partner discount information...</small>
                </CAlert>
              )}

              {/* Custom Amount Notice */}
              {useCustomAmount && (
                <CAlert color="warning" className="mb-3">
                  <small>
                    <strong>Custom Amount Enabled:</strong> Using manually
                    entered amount instead of calculated pricing
                  </small>
                </CAlert>
              )}

              {/* Tax Exemption Notice */}
              {isTaxExempt && (
                <CAlert color="success" className="mb-3">
                  <small>
                    <strong>Tax Exempt:</strong> {taxExemptReason} - No sales
                    tax will be applied
                  </small>
                </CAlert>
              )}

              <CTable striped bordered responsive>
                <CTableHead className="bg-light">
                  <CTableRow>
                    <CTableHeaderCell>Description</CTableHeaderCell>
                    <CTableHeaderCell className="text-end">
                      Unit Price
                    </CTableHeaderCell>
                    <CTableHeaderCell className="text-end">
                      Quantity
                    </CTableHeaderCell>
                    <CTableHeaderCell className="text-end">
                      Total Customer Amount
                    </CTableHeaderCell>
                    <CTableHeaderCell className="text-end">
                      Total Partner Amount
                    </CTableHeaderCell>
                    <CTableHeaderCell className="text-end">
                      Total Discount
                    </CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {/* Main Product */}
                  <CTableRow>
                    <CTableDataCell>
                      <strong>
                        {useCustomAmount
                          ? "Custom Renewal Amount"
                          : "Original Product Renewal"}
                      </strong>
                      <br />
                      <small className="text-muted">
                        {useCustomAmount
                          ? "Manually entered total amount"
                          : "Standard renewal rate"}
                      </small>
                      {isTaxExempt && (
                        <div>
                          <small className="text-success">✅ Tax Exempt</small>
                        </div>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      {useCustomAmount ? (
                        <small className="text-muted">N/A</small>
                      ) : (
                        <strong>{formatPrice(pricing.baseAmount)}</strong>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong>{quantity}</strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong>{formatPrice(pricing.customerAmount)}</strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong className="text-success">
                        {formatPrice(pricing.partnerAmount)}
                      </strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <small className="text-danger">
                        {formatPrice(pricing.discountAmount)}
                      </small>
                    </CTableDataCell>
                  </CTableRow>

                  {/* Cartridge Product */}
                  {includeCartridge && selectedCartridgeProduct && (
                    <CTableRow>
                      <CTableDataCell>
                        <strong>
                          Cartridge: {selectedCartridgeProduct.software_name}
                        </strong>
                        <br />
                        <small className="text-muted">
                          {selectedCartridgeProduct.subscription}
                        </small>
                        <br />
                        <small className="text-info">
                          ({productMarket.toUpperCase()} Market)
                        </small>
                        {isTaxExempt && (
                          <div>
                            <small className="text-success">
                              ✅ Tax Exempt
                            </small>
                          </div>
                        )}
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        <strong>
                          {formatPrice(selectedCartridgeProduct.price)}
                        </strong>
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        <strong>{quantity}</strong>
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        <strong>
                          {formatPrice(pricing.cartridgeCustomerPrice)}
                        </strong>
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        <strong className="text-success">
                          {formatPrice(pricing.cartridgePartnerPrice)}
                        </strong>
                      </CTableDataCell>
                      <CTableDataCell className="text-end">
                        <small className="text-danger">
                          {formatPrice(pricing.cartridgeDiscountAmount)}
                        </small>
                      </CTableDataCell>
                    </CTableRow>
                  )}

                  {/* Total Row */}
                  <CTableRow className="table-primary">
                    <CTableDataCell>
                      <strong>TOTAL PAYABLE AMOUNT</strong>
                      <br />
                      <small className="text-muted">
                        Final amount to be charged{" "}
                        {isTaxExempt && "(Tax Exempt)"}
                      </small>
                      {isTaxExempt && (
                        <div>
                          <small className="text-success">
                            ✅ Tax will be excluded
                          </small>
                        </div>
                      )}
                      {!isTaxExempt && (
                        <div>
                          <small className="text-muted">
                            📍 Tax will be calculated by QuickBooks
                          </small>
                        </div>
                      )}
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong>-</strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong>-</strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong className="text-primary fs-5">
                        {formatPrice(pricing.totalCustomerAmount)}
                      </strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong className="text-success fs-5">
                        {formatPrice(pricing.totalPartnerAmount)}
                      </strong>
                    </CTableDataCell>
                    <CTableDataCell className="text-end">
                      <strong className="text-danger">
                        {formatPrice(pricing.totalDiscountAmount)}
                      </strong>
                    </CTableDataCell>
                  </CTableRow>
                </CTableBody>
              </CTable>

              {/* Savings Summary */}
              {reseller && reseller.margin > 0 && (
                <CCard className="bg-success text-white mt-3">
                  <CCardBody className="py-2">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>
                          Total Savings:{" "}
                          {formatPrice(pricing.totalDiscountAmount)}
                        </strong>
                        <br />
                        <small>
                          {reseller.margin}% partner discount applied to all
                          items for {quantity} subscription(s)
                        </small>
                        {includeCartridge && selectedCartridgeProduct && (
                          <div>
                            <small>
                              Includes{" "}
                              {formatPrice(pricing.cartridgeDiscountAmount)}{" "}
                              cartridge discount
                            </small>
                          </div>
                        )}
                        {isTaxExempt && (
                          <div>
                            <small>Plus tax exemption savings</small>
                          </div>
                        )}
                      </div>
                      <CIcon icon={cilCheckCircle} size="xl" />
                    </div>
                  </CCardBody>
                </CCard>
              )}
            </CCol>
          </CRow>
        )}

        {error && (
          <CAlert color="danger" className="mt-3">
            {error}
          </CAlert>
        )}
      </>
    );
  };

  const handleRenewalSubmit = async () => {
    if (!order) {
      setError("Order information is missing. Please try again.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const pricing = calculatePricing();
      const amount = pricing.totalPartnerAmount;
      const quantity = order.quantity || 1;

      const customerEmail = order.product_owner_email || "";
      const partnerEmail = reseller?.email || order.partner_contact_email || "";
      const billingEmail = partnerEmail; // ✅ ALWAYS use partner email for billing

      // CRITICAL: For super admin, include reseller_id from the order
      const resellerId = order.reseller_id || reseller?._id;

      if (isSuperAdmin && !resellerId) {
        setError("Reseller ID is required for super admin renewals");
        setLoading(false);
        return;
      }

      // Complete dynamic request data
      const requestData = {
        // Subscription Information
        subscription_number: `RENEW-${order.subscription_number}-${Date.now()}`,
        account_name: order.account_name || "Customer",
        // product_name: order.product_name || "Software Subscription",
        product_name: order.subscription_plan || order.product_name || "Software Subscription",

        // SUPER ADMIN CRITICAL FIELD: Include reseller_id
        ...(isSuperAdmin && { reseller_id: resellerId }),

        // Amount Information
        amount: amount,
        customer_amount: pricing.totalCustomerAmount,
        partner_amount: pricing.totalPartnerAmount,
        discount: pricing.totalDiscountAmount,
        reseller_margin: reseller?.margin || 0,
        reseller_name: reseller?.name || order.reseller_name || "Reseller",

        // Custom amount information
        use_custom_amount: useCustomAmount,
        custom_amount: useCustomAmount ? customAmount : null,
        base_unit_price: order.renewal_amount_customer || 0,
        quantity: quantity,

        // Product Information
        product_id: order.product_id || "renewal",
        support_product_id: null,
        cartridge_product_id:
          includeCartridge && selectedCartridgeProduct
            ? selectedCartridgeProduct.softwareid
            : null,

        // Client Information
        client_id: order.client_id,

        // Email Information
        customer_email: customerEmail,
        partner_email: partnerEmail,
        billing_email: billingEmail,

        // Billing Information
        billing_street: order.billing_street || "",
        billing_city: order.billing_city || "",
        billing_state: order.billing_state || "",
        billing_country: order.billing_country || "",
        billing_zip: order.billing_zip || "",

        // Payment Terms
        payment_terms: paymentTerms,

        // Tax Exemption
        is_tax_exempt: isTaxExempt,
        tax_exempt_reason: taxExemptReason,

        // Additional Fields
        storage_option: "NONE",
        storage_price: 0,
        is_new_purchase: false,
        market: productMarket,

        // Renewal Specific
        original_subscription_number: order.subscription_number,
        renewal_type: "annual_renewal",
      };

      console.log("📤 Sending renewal request from super admin:", {
        isSuperAdmin: isSuperAdmin,
        reseller_id: requestData.reseller_id,
        reseller_name: requestData.reseller_name,
        order_subscription: order.subscription_number,
        // Log email configuration
        customer_email: requestData.customer_email,
        partner_email: requestData.partner_email,
        billing_email: requestData.billing_email,
        billing_email_type: "PARTNER (reseller)",
      });

      const response = await quickbooksService.createInvoice(requestData);

      if (response.success) {
        let invoiceDataToSet = null;

        if (response.invoice_data) {
          invoiceDataToSet = {
            ...response.invoice_data,
            is_net_terms: paymentTerms !== "immediate",
            payment_terms: paymentTerms,
            initial_amount_partner: pricing.totalPartnerAmount,
            initial_amount_customer: pricing.totalCustomerAmount,
            initial_discount_amount: pricing.totalDiscountAmount,
            partner_email: partnerEmail,
            billing_email: billingEmail, // ✅ Include billing email in response
            use_custom_amount: useCustomAmount,
            quantity: quantity,
          };

          if (
            paymentTerms === "immediate" &&
            response.invoice_data.invoice_id
          ) {
            startPaymentPolling(response.invoice_data.invoice_id);
          }
        } else if (response.order) {
          invoiceDataToSet = {
            is_net_terms: paymentTerms !== "immediate",
            payment_terms: paymentTerms,
            initial_amount_partner:
              response.order.renewal_amount_partner ||
              response.order.initial_amount_partner,
            initial_amount_customer:
              response.order.renewal_amount_customer ||
              response.order.initial_amount_customer,
            initial_discount_amount: response.order.initial_discount_amount,
            payment_due_date: response.order.payment_due_date,
            invoice_id: response.order.id,
            invoice_number: response.order.subscription_number,
            payment_link: null,
            customer_email: customerEmail,
            partner_email: partnerEmail,
            billing_email: billingEmail, // ✅ Include billing email in response
            use_custom_amount: useCustomAmount,
            quantity: quantity,
            amounts: {
              customer_amount:
                response.order.renewal_amount_customer ||
                response.order.initial_amount_customer,
              discount_amount: response.order.initial_discount_amount,
              partner_amount:
                response.order.renewal_amount_partner ||
                response.order.initial_amount_partner,
              total_due:
                response.order.renewal_amount_partner ||
                response.order.initial_amount_partner,
            },
          };
        }

        setInvoiceData(invoiceDataToSet);

        // Log success with email info
        console.log(`✅ Renewal created successfully!`);
        console.log(`📧 Billing emails will go to PARTNER: ${partnerEmail}`);
      } else {
        setError(response.error || "Failed to create renewal order");
        console.error("❌ Renewal creation failed:", response.error);
      }
    } catch (err) {
      console.error("❌ Renewal creation error:", err);
      setError(
        err.response?.data?.error ||
          "Failed to create renewal order. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDirectPayment = () => {
    if (invoiceData?.payment_link) {
      window.open(invoiceData.payment_link, "_blank", "noopener,noreferrer");
    }
  };

  // Render footer buttons
  const renderFooterButtons = () => {
    if (invoiceData) {
      return paymentTerms !== "immediate" ? (
        <CButton color="secondary" onClick={handleCloseModal}>
          Close
        </CButton>
      ) : null;
    } else {
      return (
        <>
          <CButton
            color="secondary"
            onClick={handleCloseModal}
            disabled={loading}
          >
            Cancel
          </CButton>
          <CButton
            color="primary"
            onClick={handleRenewalSubmit}
            disabled={
              loading ||
              (includeCartridge && !selectedCartridgeProductId) ||
              !reseller ||
              (isTaxExempt && !taxExemptReason) ||
              (useCustomAmount && (!customAmount || customAmount <= 0)) ||
              (isSuperAdmin && !order.reseller_id)
            }
          >
            {loading ? (
              <>
                <CSpinner size="sm" className="me-2" />
                Creating Renewal Order...
              </>
            ) : (
              `Create Renewal with ${reseller?.payment_mode}`
            )}
          </CButton>
        </>
      );
    }
  };

  if (!order) {
    return (
      <CModal visible={show} onClose={handleCloseModal}>
        <CModalHeader>
          <CModalTitle>Error</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <CAlert color="danger">
            Order information is not available. Please try again.
          </CAlert>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleCloseModal}>
            Close
          </CButton>
        </CModalFooter>
      </CModal>
    );
  }

  return (
    <CModal size="lg" visible={show} onClose={handleCloseModal}>
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={cilCalendar} className="me-2" />
          {invoiceData
            ? `Renewal Successful - ${order.subscription_number}`
            : `Renew Subscription - ${order.subscription_number}`}
          {isSuperAdmin && (
            <CBadge color="warning" className="ms-2">
              Super Admin Mode
            </CBadge>
          )}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {invoiceData ? (
          <SuccessView
            invoiceData={invoiceData}
            paymentTerms={paymentTerms}
            onDirectPayment={handleDirectPayment}
            onClose={handleCloseModal}
          />
        ) : (
          <ProductSelectionView />
        )}
      </CModalBody>
      <CModalFooter>{renderFooterButtons()}</CModalFooter>
    </CModal>
  );
};

export default QuickBooksRenewalModal;
